import { chooseDBTranslation } from 'utils/translations'
import {
  Table,
  TableRow,
  TableCell,
  Paragraph,
  WidthType,
  BorderStyle,
  HeightRule,
  ShadingType,
  TextRun,
  AlignmentType,
  ImageRun,
  TableLayoutType,
} from 'docx'
import { CVData } from 'types/printInterfaces'
import colors from 'constants/colors'
import { i18n } from 'i18next'
import { CountriesData } from 'api/UtilsAPI'
import { ILayout, IOrganizationColors } from 'types/layoutInterfaces'
import { toDocxColor } from './sanitize'
import { contentWidth } from './pageLayout'

/**
 * Generates a Table object based on the provided data and layout options.
 *
 * @param {string} i18n - The i18n object for localization.
 * @param {CVData} data - The data object containing CV information.
 * @param {Array} countries - The array of available countries.
 * @param {string} profileImage - The profile image data.
 * @param {string} orgLogo - The organization logo data.
 * @param {string} orgColors - The organization colors object.
 * @param {string} layout - The layout options for the table.
 * @param {string} defaultImg - The default image data.
 * @return {Table} The generated Table object.
 * @notExported
 */
const top = (
  i18n: i18n,
  data: CVData,
  countries: CountriesData,
  profileImage: ArrayBuffer | undefined,
  orgLogo: ArrayBuffer | undefined,
  orgColors: IOrganizationColors,
  layout: ILayout,
  defaultImg: ArrayBuffer | undefined
): Table => {
  const shading = {
    color: toDocxColor(orgColors?.color, colors.secondary),
    type: ShadingType.SOLID,
    fill: colors.secondaryBackground,
  }

  const borders = {
    top: {
      style: BorderStyle.NONE,
      size: 0,
      color: '#FF0000',
    },
    bottom: {
      style: BorderStyle.NONE,
      size: 0,
      color: '#FF0000',
    },
    left: {
      style: BorderStyle.NONE,
      size: 0,
      color: '#FF0000',
    },
    right: {
      style: BorderStyle.NONE,
      size: 0,
      color: '#FF0000',
    },
  }

  let image: ImageRun, logo: ImageRun

  if (profileImage) {
    image = new ImageRun({
      type: 'png',
      data: profileImage,
      transformation: {
        width: 200,
        height: 200,
      },
    })
  } else {
    image = new ImageRun({
      type: 'png',
      data: defaultImg ?? new ArrayBuffer(0),
      transformation: {
        width: 200,
        height: 200,
      },
    })
  }

  if (orgLogo) {
    logo = new ImageRun({
      type: 'png',
      data: orgLogo,
      transformation: {
        width: 100,
        height: 100,
      },
    })
  } else {
    logo = new ImageRun({
      type: 'png',
      data: defaultImg ?? new ArrayBuffer(0),
      transformation: {
        width: 100,
        height: 100,
      },
    })
  }

  const hasImage = !layout.hideProfileImage
  const imageWidth = hasImage ? 3300 : 0
  const logoWidth = 3000
  const textWidth = contentWidth - imageWidth - logoWidth
  const country = data.about.country ? countries[data.about.country.toUpperCase()] : undefined
  const raleway = { name: 'Raleway' }

  const textParagraph = (text: string | undefined, options: { bold?: boolean; color?: string } = {}) =>
    new Paragraph({
      style: 'CaleoNormal',
      children: text ? [new TextRun({ text, font: raleway, ...options })] : [],
    })

  // name, title and contact info in the same order as in the PDF
  const details = [
    new Paragraph({
      spacing: { before: 500, after: 160 },
      children: [
        new TextRun({
          text: !layout.hideName
            ? `${data.about.firstName} ${data.about.lastName}`
            : chooseDBTranslation(i18n, data.about).primaryRole,
          bold: true,
          color: toDocxColor(orgColors?.accentColor, colors.secondary),
          size: 34,
          font: raleway,
        }),
      ],
    }),
    ...(!layout.hideName ? [textParagraph(chooseDBTranslation(i18n, data.about).primaryRole, { bold: true })] : []),
    ...(layout.showContactInfo
      ? [
          textParagraph(data.about.telephone),
          textParagraph(data.about.email),
          textParagraph(data.about.streetAddress, { color: colors.gray }),
          textParagraph(
            data.about.postalCode && data.about.city && country
              ? `${data.about.postalCode} ${data.about.city}, ${country}`
              : undefined,
            { color: colors.gray }
          ),
        ]
      : [
          textParagraph(data.about.city && country ? `${data.about.city}, ${country}` : undefined, {
            color: colors.gray,
          }),
        ]),
  ]

  // The header has the same fixed height as in the PDF: a 54 pt bar and 176 pt for the details.
  // The image cell spans both rows, so the following sections always start below the image.
  return new Table({
    width: {
      size: contentWidth,
      type: WidthType.DXA,
    },
    layout: TableLayoutType.FIXED,
    columnWidths: hasImage ? [imageWidth, textWidth, logoWidth] : [textWidth, logoWidth],
    rows: [
      new TableRow({
        height: {
          value: 1080,
          rule: HeightRule.EXACT,
        },
        children: [
          ...(hasImage
            ? [
                new TableCell({
                  rowSpan: 2,
                  children: [new Paragraph({ children: [image] })],
                  width: {
                    size: imageWidth,
                    type: WidthType.DXA,
                  },
                  borders,
                }),
              ]
            : []),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: new Date().toLocaleDateString(i18n.language, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    }),
                    font: raleway,
                    size: 17,
                    bold: true,
                    color: colors.secondaryBackground,
                  }),
                ],
                spacing: {
                  before: 200,
                },
                alignment: AlignmentType.END,
              }),
            ],
            columnSpan: 2,
            borders,
            shading,
            margins: {
              right: 200,
            },
            width: {
              size: textWidth + logoWidth,
              type: WidthType.DXA,
            },
          }),
        ],
      }),
      new TableRow({
        height: {
          value: 3520,
          rule: HeightRule.ATLEAST,
        },
        children: [
          new TableCell({
            children: details,
            borders,
            margins: {
              left: hasImage ? 400 : 0,
            },
            width: {
              size: textWidth,
              type: WidthType.DXA,
            },
          }),
          new TableCell({
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                spacing: { before: 200 },
                children: [logo],
              }),
            ],
            borders,
            width: {
              size: logoWidth,
              type: WidthType.DXA,
            },
          }),
        ],
      }),
    ],
  })
}

export default top
